import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login, loginOAuth } from '../api';
import { useAuth } from '../context/AuthContext';
import ResetPasswordModal from '../components/ResetPasswordModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import ResetPasswordWithCodeModal from '../components/ResetPasswordWithCodeModal';
// Removed react-icons import due to React 19 compatibility issues
import { signInWithMicrosoft, OAuthUser } from '../utils/oauth';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [isResetWithCodeModalOpen, setIsResetWithCodeModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState<boolean>(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  //add as secret in github settings and then add inside yml
  const isLoginEnabled = process.env.REACT_APP_ENABLE_LOGIN === 'true';

  // Calculate content level based on available space
  const getContentLevel = () => {
    if (!isMobile) return 'full';
    if (viewportHeight >= 900) return 'full'; // Very large mobile screens - show everything
    if (viewportHeight >= 800) return 'extended'; // Large mobile screens - show CTA only
    if (viewportHeight >= 700) return 'standard'; // Medium mobile screens - 4 bullets
    return 'minimal'; // Small mobile screens - 3 bullets
  };

  const contentLevel = getContentLevel();

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle success message from registration
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the state to prevent showing the message again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      const data = await login(email, password);

      // Update auth context
      authLogin(data.token, data.user);

      // Redirect to dashboard
      navigate('/overview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login');
      //setError('An error occurred during login. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSuccess = (message: string) => {
    setSuccessMessage(message);
    // Clear any previous errors
    setError('');
  };

  const handleForgotPasswordSuccess = (email: string) => {
    setResetEmail(email);
    setIsForgotPasswordModalOpen(false);
    setIsResetWithCodeModalOpen(true);
    setSuccessMessage(`Reset code sent to ${email}`);
  };

  const handleResetPasswordSuccess = (message: string) => {
    setSuccessMessage(message);
    setIsResetWithCodeModalOpen(false);
    setResetEmail('');
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');

      if (!credentialResponse.credential) {
        throw new Error('No credential returned from Google');
      }

      type GoogleJwtPayload = {
        email: string;
        given_name?: string;
        family_name?: string;
        sub: string;
      };

      const payload = jwtDecode<GoogleJwtPayload>(credentialResponse.credential);

      const data = await loginOAuth({
        email: payload.email,
        firstName: payload.given_name,
        lastName: payload.family_name,
        oauthProvider: 'google',
        oauthProviderUserId: payload.sub
      });

      authLogin(data.token, data.user);
      navigate('/overview');
    } catch (err) {
      console.error('Google login error:', err);
      setError(err instanceof Error ? err.message : 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed');
  };

  // Reference to trigger the hidden GoogleLogin component
  const googleLoginRef = useRef<HTMLDivElement>(null);

  const handleGoogleLoginClick = () => {
    // Trigger the hidden GoogleLogin component
    const googleButton = googleLoginRef.current?.querySelector('div[role="button"]') as HTMLElement;
    if (googleButton) {
      googleButton.click();
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const microsoftUser: OAuthUser = await signInWithMicrosoft();

      // Login with OAuth
      const data = await loginOAuth({
        email: microsoftUser.email,
        firstName: microsoftUser.firstName,
        lastName: microsoftUser.lastName,
        oauthProvider: microsoftUser.provider,
        oauthProviderUserId: microsoftUser.providerId
      });

      // Update auth context
      authLogin(data.token, data.user);

      // Redirect to dashboard
      navigate('/overview');
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError(err instanceof Error ? err.message : 'Microsoft login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-start' : 'center',
      padding: isMobile ? '0' : '20px 0'
    }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: '1200px',
          minHeight: '100vh',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: isMobile ? 'auto' : 'visible'
        }}
        className={isMobile ? 'mobile-scrollable mobile-force-scroll' : ''}
      >
        <style>
          {`
          @keyframes slideInFromRight {
            from {
              transform: translateX(30px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .form-container {
            animation: slideInFromRight 0.6s ease-out;
          }
          
          .left-panel-content {
            animation: fadeIn 0.6s ease-out 0.2s both;
          }

          @media (max-width: 767px) {
            .mobile-stack {
              flex-direction: column !important;
            }
            .mobile-hide {
              display: none !important;
            }
            .mobile-compact {
              padding: 20px !important;
              
            }
            .mobile-text-small {
              font-size: 14px !important;
            }
            .mobile-text-medium {
              font-size: 16px !important;
            }
            .mobile-text-large {
              font-size: 24px !important;
            }
            .mobile-scrollable {
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
          }

          @media (max-width: 767px) and (max-height: 600px) {
            .mobile-force-scroll {
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
            }
          }

          @media (max-width: 1024px) {
            .tablet-reduce {
              padding: 30px !important;
            }
            .tablet-text {
              font-size: 14px !important;
            }
          }
        `}
        </style>
        {/* Left side - Information panel */}
        <div style={{
          flex: isMobile ? 'none' : 1,
          minHeight: isMobile ? 'auto' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: isMobile ?
            (contentLevel === 'full' ? '20px 20px 12px 20px' :
              contentLevel === 'extended' ? '18px 20px 10px 20px' :
                contentLevel === 'standard' ? '12px 20px 6px 20px' : '8px 20px 2px 20px')
            : isTablet ? '40px 30px' : '60px 40px',
          color: 'white',
          position: 'relative'
        }}
          className={isMobile ? 'mobile-compact' : ''}
        >
          <div className="left-panel-content" style={{
            maxWidth: isMobile ? '100%' : '400px',
            textAlign: 'center'
          }}>
            {/* Logo */}
            <div style={{
              marginBottom: isMobile ?
                (contentLevel === 'full' ? '12px' :
                  contentLevel === 'extended' ? '10px' :
                    contentLevel === 'standard' ? '6px' : '4px')
                : '40px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: isMobile ? '0 auto 22px' : '0 auto 16px'
              }}>
                <img
                  src="/execdash_logo_trans.png"
                  alt="ExecDash Logo"
                  style={{
                    height: isMobile ?'68px' : '80px',
                    width: 'auto',
                    filter: 'brightness(0) invert(1)' // Make logo white for dark background
                  }}
                />
              </div>

            </div>

            {/* Features list - Dynamic based on content level */}
            {isMobile ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                textAlign: 'center',
                marginBottom: contentLevel === 'full' ? '12px' :
                  contentLevel === 'extended' ? '10px' :
                    contentLevel === 'standard' ? '6px' : '4px'
              }}>
                {/* Bullet Point 1 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    Streamline your next leadership meeting with targeted priorities
                  </span>
                </div>

                {/* Bullet Point 2 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    Unify business data into a single executive view
                  </span>
                </div>

                {/* Bullet Point 3 - Always shown */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  marginBottom: contentLevel === 'full' ? '6px' :
                    contentLevel === 'extended' ? '5px' :
                      contentLevel === 'standard' ? '3px' : '2px',
                  width: '100%'
                }}>
                  <div style={{
                    width: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    height: contentLevel === 'full' ? '18px' :
                      contentLevel === 'extended' ? '16px' :
                        contentLevel === 'standard' ? '15px' : '14px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: contentLevel === 'full' ? '10px' :
                      contentLevel === 'extended' ? '8px' :
                        contentLevel === 'standard' ? '7px' : '6px',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '9px' :
                        contentLevel === 'extended' ? '8px' :
                          contentLevel === 'standard' ? '7px' : '7px'
                    }}>✓</span>
                  </div>
                  <span style={{
                    fontSize: contentLevel === 'full' ? '14px' :
                      contentLevel === 'extended' ? '13px' :
                        contentLevel === 'standard' ? '13px' : '12px',
                    lineHeight: contentLevel === 'full' ? '1.4' :
                      contentLevel === 'extended' ? '1.3' :
                        contentLevel === 'standard' ? '1.25' : '1.2',
                    fontWeight: 500,
                    flex: 1,
                    textAlign: 'center'
                  }}>
                    Surface key trends, risks, and opportunities with AI-augmented insights
                  </span>
                </div>

                {/* Bullet Point 4 - Shown on standard and above */}
                {(contentLevel === 'full' || contentLevel === 'extended' || contentLevel === 'standard') && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    marginBottom: contentLevel === 'full' ? '8px' :
                      contentLevel === 'extended' ? '6px' : '4px',
                    width: '100%'
                  }}>
                    <div style={{
                      width: contentLevel === 'full' ? '18px' :
                        contentLevel === 'extended' ? '16px' : '15px',
                      height: contentLevel === 'full' ? '18px' :
                        contentLevel === 'extended' ? '16px' : '15px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: contentLevel === 'full' ? '10px' :
                        contentLevel === 'extended' ? '8px' : '7px',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      <span style={{
                        fontSize: contentLevel === 'full' ? '9px' :
                          contentLevel === 'extended' ? '8px' : '7px'
                      }}>✓</span>
                    </div>
                    <span style={{
                      fontSize: contentLevel === 'full' ? '15px' :
                        contentLevel === 'extended' ? '14px' : '13px',
                      lineHeight: contentLevel === 'full' ? '1.4' :
                        contentLevel === 'extended' ? '1.3' : '1.25',
                      fontWeight: 500,
                      flex: 1,
                      textAlign: 'center'
                    }}>
                      Get started in minutes with smart defaults and role-specific views
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                textAlign: 'left',
                marginBottom: '40px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Streamline your next leadership meeting with targeted priorities                  
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Unify business data into a single executive view
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Surface key trends, risks, and opportunities with AI-augmented insights
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '12px' }}>✓</span>
                  </div>
                  <span style={{ fontSize: '16px', lineHeight: '1.5' }}>
                    Get started in minutes with smart defaults and role-specific views
                  </span>
                </div>
              </div>
            )}

            {/* Trust indicators - Show on full content level only */}
            {((!isMobile) || (isMobile && (contentLevel === 'full' || contentLevel === 'standard' || contentLevel === 'extended'))) && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: isMobile ? '26px' : '24px',
                opacity: 0.8,
                marginTop: isMobile ? '20px' : '0'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Built</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>For Leaders</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>By Leaders</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Exec Rating</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>4.8/5</div>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px'
                  }}>★★★★★</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px',
                    marginBottom: '4px'
                  }}>Uptime</div>
                  <div style={{
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 600
                  }}>99.9%</div>
                  <div style={{
                    fontSize: isMobile ? '10px' : '12px'
                  }}>Guaranteed</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Login Form */}
        <div style={{
          flex: isMobile ? 0 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ?
            (contentLevel === 'full' ? '6px 16px 12px 16px' :
              contentLevel === 'extended' ? '5px 16px 10px 16px' :
                contentLevel === 'standard' ? '3px 16px 6px 16px' : '2px 16px 4px 16px')
            : isTablet ? '30px' : '40px'
        }}
          className={isMobile ? 'mobile-compact' : isTablet ? 'tablet-reduce' : ''}
        >
          <div className="form-container" style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px',
            backgroundColor: 'white',
            padding: isMobile ?
              (contentLevel === 'full' ? '12px' :
                contentLevel === 'extended' ? '11px' :
                  contentLevel === 'standard' ? '10px' : '8px')
              : isTablet ? '36px' : '48px',
            borderRadius: isMobile ? '6px' : '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            margin: isMobile ? '0' : '0'
          }}>
            <div style={{
              marginBottom: isMobile ?
                (contentLevel === 'full' ? '16px' :
                  contentLevel === 'extended' ? '14px' :
                    contentLevel === 'standard' ? '10px' : '8px')
                : '32px'
            }}>
              <h2 style={{
                fontSize: isMobile ?
                  (contentLevel === 'full' ? '20px' :
                    contentLevel === 'extended' ? '19px' :
                      contentLevel === 'standard' ? '17px' : '16px')
                  : '30px',
                fontWeight: 700,
                color: '#111827',
                margin: isMobile ?
                  (contentLevel === 'full' ? '0 0 4px 0' :
                    contentLevel === 'extended' ? '0 0 3px 0' :
                      contentLevel === 'standard' ? '0 0 2px 0' : '0 0 1px 0')
                  : '0 0 8px 0',
                letterSpacing: '-0.02em'
              }}
                className={isMobile ? 'mobile-text-large' : ''}
              >
                Welcome back
              </h2>
              <p style={{
                fontSize: isMobile ?
                  (contentLevel === 'full' ? '14px' :
                    contentLevel === 'extended' ? '13px' :
                      contentLevel === 'standard' ? '12px' : '11px')
                  : '16px',
                color: '#6b7280',
                margin: 0
              }}
                className={isMobile ? 'mobile-text-small' : ''}
              >
                Please sign in to your account
              </p>
            </div>

            {error && (
              <div style={{
                padding: '12px',
                borderRadius: '4px',
                backgroundColor: '#ffecec',
                color: '#d63031',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            {successMessage && (
              <div style={{
                padding: '12px',
                borderRadius: '4px',
                backgroundColor: '#e3fcef',
                color: '#2ecc71',
                marginBottom: '20px'
              }}>
                {successMessage}
              </div>
            )}

            {/* Login Disabled Message */}
            {!isLoginEnabled && (
              <div style={{
                padding: '12px',
                borderRadius: '4px',
                backgroundColor: '#fff3cd',
                color: '#856404',
                marginBottom: isMobile ?
                  (contentLevel === 'full' ? '16px' :
                    contentLevel === 'extended' ? '14px' :
                      contentLevel === 'standard' ? '10px' : '8px')
                  : '24px',
                textAlign: 'center',
                border: '1px solid #ffeaa7'
              }}>
                Login is currently disabled. Sorry for the inconvenience.
              </div>
            )}

            {/* OAuth Buttons */}
            <div style={{
              marginBottom: isMobile ?
                (contentLevel === 'full' ? '16px' :
                  contentLevel === 'extended' ? '14px' :
                    contentLevel === 'standard' ? '10px' : '8px')
                : '24px'
            }}>
              <button
                type="button"
                onClick={handleGoogleLoginClick}
                disabled={isLoading || !isLoginEnabled}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '12px 16px',
                  backgroundColor: !isLoginEnabled ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '14px' : '14px',
                  fontWeight: 500,
                  color: !isLoginEnabled ? '#9ca3af' : '#374151',
                  cursor: (isLoading || !isLoginEnabled) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: isMobile ?
                    (contentLevel === 'full' ? '8px' :
                      contentLevel === 'extended' ? '7px' :
                        contentLevel === 'standard' ? '5px' : '4px')
                    : '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: isMobile ?
                    (contentLevel === 'full' ? '42px' :
                      contentLevel === 'extended' ? '40px' :
                        contentLevel === 'standard' ? '36px' : '34px')
                    : 'auto'
                }}
                onMouseOver={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
              >
                <span style={{ color: !isLoginEnabled ? '#9ca3af' : '#db4437', fontSize: '16px', fontWeight: 'bold' }}>G</span>
                Sign in with Google
              </button>

              {/* Hidden GoogleLogin component for actual authentication */}
              <div ref={googleLoginRef} style={{ display: 'none' }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} useOneTap={false} />
              </div>

              <button
                type="button"
                onClick={handleMicrosoftLogin}
                disabled={isLoading || !isLoginEnabled}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '12px 16px',
                  backgroundColor: !isLoginEnabled ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '14px' : '14px',
                  fontWeight: 500,
                  color: !isLoginEnabled ? '#9ca3af' : '#374151',
                  cursor: (isLoading || !isLoginEnabled) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: isMobile ?
                    (contentLevel === 'full' ? '42px' :
                      contentLevel === 'extended' ? '40px' :
                        contentLevel === 'standard' ? '36px' : '34px')
                    : 'auto'
                }}
                onMouseOver={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
              >
                <span style={{ color: !isLoginEnabled ? '#9ca3af' : '#00a1f1', fontSize: '16px' }}>⊞</span>
                Sign in with Microsoft
              </button>
            </div>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: isMobile ? '12px' : '24px'
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#e5e7eb'
              }}></div>
              <span style={{
                padding: isMobile ? '0 10px' : '0 16px',
                fontSize: isMobile ? '11px' : '14px',
                color: '#9ca3af',
                backgroundColor: 'white'
              }}
                className={isMobile ? 'mobile-text-small' : ''}
              >
                Or sign in with email
              </span>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#e5e7eb'
              }}></div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div style={{ marginBottom: isMobile ? '10px' : '20px' }}>
                <label htmlFor="email" style={{
                  display: 'block',
                  marginBottom: isMobile ? '3px' : '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: 500,
                  color: '#374151'
                }}>Email Address *</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '8px 10px' : '12px 16px',
                    borderRadius: '4px',
                    border: '1px solid #d1d5db',
                    fontSize: isMobile ? '16px' : '14px',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box',
                    minHeight: isMobile ? '36px' : 'auto'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  required
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: isMobile ? '8px' : '20px' }}>
                <label htmlFor="password" style={{
                  display: 'block',
                  marginBottom: isMobile ? '3px' : '6px',
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: 500,
                  color: '#374151'
                }}>Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: isMobile ? '8px 40px 8px 10px' : '12px 40px 12px 16px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      fontSize: isMobile ? '16px' : '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box',
                      minHeight: isMobile ? '36px' : 'auto'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      fontSize: '14px',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading || !isLoginEnabled}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '14px 16px',
                  background: (isLoading || !isLoginEnabled) ? '#9ca3af' : 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: 600,
                  cursor: (isLoading || !isLoginEnabled) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  marginBottom: isMobile ? '8px' : '16px',
                  minHeight: isMobile ? '36px' : 'auto'
                }}
                onMouseOver={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isLoading && isLoginEnabled) {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>

              {/* Forgot Password Link */}
              <div style={{ textAlign: 'center', marginBottom: isMobile ? '8px' : '20px' }}>
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordModalOpen(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 500
                  }}
                  onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  Forgot your password?
                </button>
              </div>
            </form>

            {/* Sign up link */}
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280' }}>
                Don't have an account?{' '}
                <Link
                  to="/register"
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  Sign up
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Password Reset Modals */}
      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
        onSuccess={handleForgotPasswordSuccess}
      />
      
      <ResetPasswordWithCodeModal
        isOpen={isResetWithCodeModalOpen}
        email={resetEmail}
        onClose={() => {
          setIsResetWithCodeModalOpen(false);
          setResetEmail('');
        }}
        onSuccess={handleResetPasswordSuccess}
      />
      
      {/* Admin Reset Password Modal (keep for admin use) */}
      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
        onSuccess={handleRegisterSuccess}
      />
    </div>
  );
};

export default Login;
